// Control-surface workbench client (aircraft CS injection — segment lifting
// surfaces, extract spanwise sections, save named CS variants, bake true cuts).
//
// Server: thread/thread-api/app/routes/geometry.py (+examples/+segment/+sections)
// and thread/thread-api/app/routes/control_surface.py (variants + cut), all under
// the project-scoped prefix:
//   GET  /projects/{id}/geometry/examples
//   POST /projects/{id}/geometry/segment
//   POST /projects/{id}/geometry/sections
//   POST /projects/{id}/geometry/tessellate
//   POST /projects/{id}/control-surface/variants?branch=
//   GET  /projects/{id}/control-surface/variants?branch=
//   POST /projects/{id}/control-surface/cut
//   GET  /projects/{id}/control-surface/cut/{job_id}
//   POST /projects/{id}/control-surface/cut/{job_id}/cancel
//   GET  /projects/{id}/control-surface/cut          (recent jobs, rehydrate)
//
// Sections come back keyed by eta formatted '%.4f' (e.g. '0.2500'), contours
// ordered LE->TE — the same shape features/highlift/geometry/csEngine consumes.

import { apiClient } from "./client";
import type { CsSections } from "../features/highlift/geometry/csEngine";

// --- examples --------------------------------------------------------------------

/** One example geometry the backend exposes for segmentation (a file scanned from
 *  THREAD_GEOMETRY_EXAMPLES_DIR on the API host). */
export interface ExampleGeometry {
  /** Stable id - the file stem (e.g. 'cessna172'). */
  key: string;
  /** Human label (e.g. 'Cessna 172'). */
  label: string;
  /** Absolute path on the API host - feed it straight to step_path. */
  path: string;
  kind: "step" | "stl";
  size_mb: number;
  /** 'aircraft-screen' when the stem matches an Aircraft-screen model, else by extension. */
  group: "aircraft-screen" | "step" | "stl";
}

export interface ExamplesResponse {
  examples: ExampleGeometry[];
  /** The scanned directory, or null when the examples dir is not configured. */
  dir: string | null;
}

/** List the backend's example geometries (empty list + null dir when unconfigured). */
export async function listExamples(projectId: string): Promise<ExamplesResponse> {
  return apiClient<ExamplesResponse>(`/projects/${projectId}/geometry/examples`);
}

// --- segmentation --------------------------------------------------------------

/** One segmented lifting surface: an axis-aligned bbox + which axis is span. */
export interface SegSurface {
  /** Classifier label, e.g. 'wing' / 'htail' / 'vtail'. */
  label: string;
  /** [xmin, ymin, zmin, xmax, ymax, zmax] in model units. */
  bbox: [number, number, number, number, number, number];
  /** 'y' for planar surfaces (wing/tail), 'z' for the fin. */
  span_axis: "y" | "z";
  /** True when the bbox covers both semispans (a full mirrored wing). */
  mirrored?: boolean;
  /** Classifier confidence in [0, 1], when the backend reports one. */
  confidence?: number;
}

export interface SegmentRequest {
  assemblyId?: string;
  stepPath?: string;
}

export interface SegmentResponse {
  surfaces: SegSurface[];
  /**
   * Aircraft-level z-orientation of the source geometry: +1 when model +z is aerodynamic UP
   * (a z-up STEP, e.g. dornier228), -1 when the model is authored z-DOWN (e.g. cessna172 -
   * its fin extends to NEGATIVE z). Inferred server-side (fin direction, else a wing camber
   * cue) because nothing in a STEP declares it. DISPLAY-ONLY: the engine, bake and stored
   * coords stay in the MODEL frame - consumers flip what they DRAW when this is -1.
   * Absent on older servers - treat as +1.
   */
  up_sign?: 1 | -1;
  cached?: boolean;
}

/** Segment an aircraft assembly/STEP into labeled lifting-surface bboxes. */
export async function segmentAircraft(
  projectId: string,
  req: SegmentRequest,
): Promise<SegmentResponse> {
  return apiClient<SegmentResponse>(`/projects/${projectId}/geometry/segment`, {
    method: "POST",
    body: { assembly_id: req.assemblyId ?? null, step_path: req.stepPath ?? null },
  });
}

// --- sections ------------------------------------------------------------------

export interface SectionsRequest {
  assemblyId?: string;
  stepPath?: string;
  /** The segmented surface to slice (echoed back to the extractor). */
  surface: SegSurface;
  /** Station count (default 21 server-side). */
  n?: number;
}

export interface SectionsResponse {
  /** Keys are eta formatted '%.4f'; contours ordered LE->TE. */
  sections: CsSections;
  /** Millimetres per STEP model unit of the section coords (1.0 for a mm-authored STEP,
   *  1000.0 for metres, 304.8 for feet). Absent on older servers - treat as 1. */
  unit_mm?: number;
  cached?: boolean;
}

/** Extract spanwise airfoil sections across one segmented surface. */
export async function fetchSections(
  projectId: string,
  req: SectionsRequest,
): Promise<SectionsResponse> {
  return apiClient<SectionsResponse>(`/projects/${projectId}/geometry/sections`, {
    method: "POST",
    body: {
      assembly_id: req.assemblyId ?? null,
      step_path: req.stepPath ?? null,
      surface: req.surface,
      n: req.n ?? 21,
    },
  });
}

// --- tessellation (display mesh for the 3D pane) ---------------------------------

/** Whole-aircraft triangle mesh: shared vertex list + index triples (welded, 2dp). */
export interface TessMesh {
  /** Vertex positions [x, y, z] in the CAD's model units. */
  v: [number, number, number][];
  /** Triangle vertex-index triples into v. */
  t: [number, number, number][];
}

export interface TessellateRequest {
  assemblyId?: string;
  stepPath?: string;
  /** Only 'display' exists today (BRepMesh deflection = extent/420 server-side). */
  quality?: "display";
}

export interface TessellateResponse {
  mesh: TessMesh;
  cached?: boolean;
}

/** Tessellate the whole aircraft into a display-grade triangle mesh (sidecar-cached
 *  server-side; the C172 STEP lands around a few MB). */
export async function tessellateGeometry(
  projectId: string,
  req: TessellateRequest,
): Promise<TessellateResponse> {
  return apiClient<TessellateResponse>(`/projects/${projectId}/geometry/tessellate`, {
    method: "POST",
    body: {
      assembly_id: req.assemblyId ?? null,
      step_path: req.stepPath ?? null,
      quality: req.quality ?? "display",
    },
  });
}

// --- variants (thread_core persistence) -----------------------------------------

/** A saved control-surface design variant (full param record, replayable). */
export interface CsVariantInput {
  name: string;
  /** The segmented surface label the variant applies to (e.g. 'wing'). */
  surface: string;
  /** aileron / elevator / rudder / flap / ... */
  csType: string;
  params: Record<string, number | string | boolean>;
}

export interface CsVariantRecord {
  name: string;
  surface: string;
  csType: string;
  params: Record<string, unknown>;
  nodePath: string;
  createdAt?: string;
}

/** Persist a named CS variant under the project (thread_core node) on the given branch. */
export async function saveVariant(
  projectId: string,
  v: CsVariantInput,
  branch = "main",
): Promise<{ nodePath: string }> {
  const params = new URLSearchParams({ branch });
  const res = await apiClient<{ node_path: string }>(
    `/projects/${projectId}/control-surface/variants?${params.toString()}`,
    {
      method: "POST",
      body: { name: v.name, surface: v.surface, cs_type: v.csType, params: v.params },
    },
  );
  return { nodePath: res.node_path };
}

/** Raw variant row as the API returns it (snake_case). */
interface CsVariantRow {
  name: string;
  surface: string;
  cs_type: string;
  params: Record<string, unknown>;
  node_path: string;
  created_at?: string;
}

/** List the project's saved CS variants on the given branch. */
export async function listVariants(
  projectId: string,
  branch = "main",
): Promise<{ variants: CsVariantRecord[] }> {
  const params = new URLSearchParams({ branch });
  const res = await apiClient<{ variants: CsVariantRow[] }>(
    `/projects/${projectId}/control-surface/variants?${params.toString()}`,
  );
  return {
    variants: (res.variants ?? []).map((row) => ({
      name: row.name,
      surface: row.surface,
      csType: row.cs_type,
      params: row.params ?? {},
      nodePath: row.node_path,
      createdAt: row.created_at,
    })),
  };
}

// --- bake (true CAD cut) ---------------------------------------------------------

/** One control to cut into the source CAD (all lengths in mm). */
export interface BakeCutControl {
  name: string;
  surface: SegSurface;
  eta1: number;
  eta2: number;
  xc_hinge: number;
  gap_mm: number;
  fillet_mm?: number;
  yh?: number;
  /** true/false force; 'auto' mirrors when the surface bbox spans both semispans. */
  mirror?: boolean | "auto";
  deflections_deg?: number[];
}

export interface BakeCutRequest {
  assemblyId?: string;
  stepPath?: string;
  controls: BakeCutControl[];
}

/** POST /cut acknowledgement (the route's CutStartResponse) - a job HANDLE,
 *  not geometry. Poll getCutJob(job_id) for the baked result. */
export interface BakeCutStartResponse {
  /** Poll handle, or null when the cut was skipped (geometry feature off). */
  job_id: string | null;
  /** 'pending' when a job started; 'skipped' when feature_enabled is false. */
  status: string;
  feature_enabled: boolean;
  /** Expanded control names the job will attempt (mirrors included). */
  controls: string[];
  note?: string | null;
}

/** One baked STEP on disk - the cut re-assembled at one deflection. The path
 *  is absolute on the API host and feeds straight into /geometry/tessellate
 *  (whose sidecar cache keys on content hash, so each cut caches once). */
export interface BakeCutOutput {
  deflection_deg: number | null;
  step_path: string;
}

/** Per-control cut result: the echoed request geometry + the baked outputs.
 *  Mirrors the route's CutControlResult - the backend pins this shape in
 *  tests/test_control_surface_routes.py. */
export interface BakeCutControlResult {
  name: string;
  surface?: string | null;
  is_mirror?: boolean;
  eta1: number;
  eta2: number;
  xc_hinge: number;
  gap_mm: number;
  fillet_mm?: number;
  outputs: BakeCutOutput[];
}

/** One control that failed to cut (the batch keeps going without it). */
export interface BakeCutError {
  name: string;
  surface?: string | null;
  error: string;
}

/** Completed-job payload: per-control results + per-control failures. */
export interface BakeCutJobResult {
  controls: BakeCutControlResult[];
  errors: BakeCutError[];
  total: number;
  completed: number;
}

export interface BakeCutJobStatus {
  job_id: string;
  /** 'cancelled' is terminal - the job honored a cancel request between controls
   *  (outputs already produced are kept in result). */
  status: "pending" | "processing" | "complete" | "error" | "cancelled";
  progress: number;
  message: string;
  result: BakeCutJobResult | null;
  error: string | null;
}

/** Bake the control-surface cut(s) into real CAD. The route is job-shaped:
 * this returns a handle immediately; the OCC work (~10s per control) runs
 * server-side and getCutJob polls it to the baked STEP paths. */
export async function bakeCut(
  projectId: string,
  req: BakeCutRequest,
): Promise<BakeCutStartResponse> {
  return apiClient<BakeCutStartResponse>(`/projects/${projectId}/control-surface/cut`, {
    method: "POST",
    body: {
      assembly_id: req.assemblyId ?? null,
      step_path: req.stepPath ?? null,
      controls: req.controls,
    },
  });
}

/** Poll a batch-cut job; a 'complete' status carries the baked STEP paths. */
export async function getCutJob(
  projectId: string,
  jobId: string,
): Promise<BakeCutJobStatus> {
  return apiClient<BakeCutJobStatus>(
    `/projects/${projectId}/control-surface/cut/${jobId}`,
  );
}

/** Request cancellation of a running cut job. The backend sets a flag the job
 *  checks BETWEEN controls (it cannot kill a mid-cut OCC subprocess), so the
 *  poll continues until the job reports status 'cancelled'. An already-terminal
 *  job is a no-op that echoes its current status. */
export async function cancelCutJob(
  projectId: string,
  jobId: string,
): Promise<{ job_id: string; status: string }> {
  return apiClient<{ job_id: string; status: string }>(
    `/projects/${projectId}/control-surface/cut/${jobId}/cancel`,
    { method: "POST", body: {} },
  );
}
